import React, { useEffect, useState } from 'react';
import { Download, MoreVertical, Share, X } from 'lucide-react';

const STORAGE_KEY = 'nexohub-install-prompt-dismissed';

function isStandaloneMode() {
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    window.navigator.standalone === true
  );
}

function isDesktopViewport() {
  return window.matchMedia('(min-width: 1024px)').matches;
}

function isAppleMobileDevice() {
  return /iPhone|iPad|iPod/i.test(window.navigator.userAgent) ||
    (window.navigator.platform === 'MacIntel' && window.navigator.maxTouchPoints > 1);
}

function PwaInstallPrompt() {
  const [installPrompt, setInstallPrompt] = useState(null);
  const [isVisible, setIsVisible] = useState(false);
  const [canShowManualHelp, setCanShowManualHelp] = useState(false);
  const isAppleMobile = isAppleMobileDevice();

  useEffect(() => {
    if (isStandaloneMode() || (isDesktopViewport() && !isAppleMobile)) {
      return undefined;
    }

    const hasDismissed = window.localStorage.getItem(STORAGE_KEY) === 'true';
    let timer;

    if (!hasDismissed) {
      timer = window.setTimeout(() => {
        setCanShowManualHelp(true);
        setIsVisible(true);
      }, 300);
    }

    const handleBeforeInstallPrompt = (event) => {
      event.preventDefault();
      if (isDesktopViewport() && !isAppleMobile) {
        return;
      }
      if (timer) {
        window.clearTimeout(timer);
      }
      setInstallPrompt(event);
      setIsVisible(true);
      setCanShowManualHelp(false);
    };

    const handleAppInstalled = () => {
      setInstallPrompt(null);
      setIsVisible(false);
      setCanShowManualHelp(false);
      window.localStorage.setItem(STORAGE_KEY, 'true');
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      if (timer) {
        window.clearTimeout(timer);
      }
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, [isAppleMobile]);

  const handleInstall = async () => {
    if (!installPrompt) return;

    installPrompt.prompt();
    await installPrompt.userChoice;
    setInstallPrompt(null);
    setIsVisible(false);
  };

  const handleDismiss = () => {
    setIsVisible(false);
    window.localStorage.setItem(STORAGE_KEY, 'true');
  };

  if (!isVisible || (!installPrompt && !canShowManualHelp)) {
    return null;
  }

  return (
    <div className="fixed left-4 right-4 top-[calc(env(safe-area-inset-top)+1rem)] z-[90] mx-auto flex max-w-md items-center gap-3 rounded-lg border border-slate-200 bg-white p-3 shadow-xl">
      <div className="flex-1">
        <p className="text-sm font-semibold text-slate-900">Instalar NexoHub</p>
        <p className="text-xs text-slate-500">
          {isAppleMobile
            ? 'En Safari, pulsa Compartir y luego Agregar a pantalla de inicio.'
            : !installPrompt
            ? 'Abre el menu del navegador y selecciona Instalar app o Agregar a pantalla de inicio.'
            : 'Agrega la app a tu pantalla de inicio para abrirla rapido.'}
        </p>
      </div>
      {isAppleMobile ? (
        <div className="inline-flex h-9 shrink-0 items-center gap-2 rounded-md bg-slate-100 px-3 text-sm font-semibold text-slate-700">
          <Share size={16} />
          Compartir
        </div>
      ) : installPrompt ? (
        <button
          type="button"
          onClick={handleInstall}
          className="inline-flex h-9 items-center gap-2 rounded-md bg-indigo-600 px-3 text-sm font-semibold text-white hover:bg-indigo-700"
        >
          <Download size={16} />
          Instalar
        </button>
      ) : (
        <div className="inline-flex h-9 shrink-0 items-center gap-1 rounded-md bg-slate-100 px-2 text-sm font-semibold text-slate-700">
          <MoreVertical size={16} />
          Menú
        </div>
      )}
      <button
        type="button"
        aria-label="Cerrar"
        onClick={handleDismiss}
        className="inline-flex h-9 w-9 items-center justify-center rounded-md text-slate-500 hover:bg-slate-100 hover:text-slate-900"
      >
        <X size={16} />
      </button>
    </div>
  );
}

export default PwaInstallPrompt;
